import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, Area, AreaChart,
  Cell
} from 'recharts';

const COLORS = {
  primary: '#1B2A4A',
  gold: '#B8860B',
  green: '#10B981',
  red: '#EF4444',
  gray: '#9CA3AF',
  lightGray: '#F3F4F6',
};

function scoreColor(score) {
  if (score >= 60) return COLORS.green;
  if (score >= 40) return COLORS.gold;
  return COLORS.red;
}

const MONTHS_FR = { '01': 'Jan', '02': 'Fév', '03': 'Mar', '04': 'Avr', '05': 'Mai', '06': 'Jun', '07': 'Jul', '08': 'Aoû', '09': 'Sep', '10': 'Oct', '11': 'Nov', '12': 'Déc' };

export function PositioningChart({ data }) {
  if (!data || data.length === 0) return null;
  const sorted = [...data].sort((a, b) => Math.abs(b.score - 50) - Math.abs(a.score - 50)).slice(0, 10);

  return (
    <div data-testid="positioning-chart">
      <ResponsiveContainer width="100%" height={Math.max(200, sorted.length * 36)}>
        <BarChart data={sorted} layout="vertical" margin={{ left: 10, right: 30, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10, fill: COLORS.gray }} />
          <YAxis type="category" dataKey="label" width={140} tick={{ fontSize: 10, fill: COLORS.primary }} />
          <Tooltip
            formatter={(v) => [`${v}/100`, 'Score']}
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          <ReferenceLine x={50} stroke={COLORS.gray} strokeDasharray="3 3" label={{ value: 'Médiane', position: 'top', fontSize: 9, fill: COLORS.gray }} />
          <Bar dataKey="score" radius={[0, 4, 4, 0]} barSize={18}>
            {sorted.map((entry, i) => (
              <Cell key={i} fill={scoreColor(entry.score)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RadarPositionChart({ data }) {
  if (!data || data.length < 3) return null;
  const selected = [...data].sort((a, b) => a.score - b.score).slice(0, 8).map(d => ({
    ...d,
    shortLabel: d.label.length > 16 ? d.label.slice(0, 14) + '...' : d.label,
    median: 50,
  }));

  return (
    <div data-testid="radar-chart">
      <ResponsiveContainer width="100%" height={320}>
        <RadarChart data={selected} cx="50%" cy="50%" outerRadius="75%">
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis dataKey="shortLabel" tick={{ fontSize: 9, fill: COLORS.primary }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 8, fill: COLORS.gray }} />
          <Radar name="Médiane" dataKey="median" stroke={COLORS.gray} fill={COLORS.gray} fillOpacity={0.05} strokeDasharray="3 3" />
          <Radar name="Votre score" dataKey="score" stroke={COLORS.primary} fill={COLORS.primary} fillOpacity={0.15} strokeWidth={2} />
          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ScoreEvolutionChart({ data }) {
  if (!data || data.length < 2) return null;
  const formatted = data.map(d => {
    const [y, m] = d.period.split('-');
    return { ...d, label: `${MONTHS_FR[m] || m} ${y.slice(2)}` };
  });

  return (
    <div data-testid="score-evolution-chart">
      <ResponsiveContainer width="100%" height={220}>
        <AreaChart data={formatted} margin={{ left: 0, right: 10, top: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.15} />
              <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: COLORS.gray }} />
          <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: COLORS.gray }} />
          <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8 }} formatter={(v) => [`${v}/100`, 'Score']} />
          <ReferenceLine y={50} stroke={COLORS.gray} strokeDasharray="3 3" label={{ value: 'Médiane', position: 'right', fontSize: 9, fill: COLORS.gray }} />
          <Area type="monotone" dataKey="score" stroke={COLORS.primary} fill="url(#scoreGrad)" strokeWidth={2} dot={{ r: 4, fill: COLORS.gold, stroke: COLORS.primary, strokeWidth: 1.5 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function LeversChart({ data }) {
  if (!data || data.length === 0) return null;
  const top = data.slice(0, 6).map(d => ({ ...d, label: d.label.length > 28 ? d.label.slice(0, 26) + '...' : d.label }));

  return (
    <div data-testid="levers-chart">
      <ResponsiveContainer width="100%" height={Math.max(160, top.length * 36)}>
        <BarChart data={top} layout="vertical" margin={{ left: 10, right: 40, top: 5, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 10, fill: COLORS.gray }} tickFormatter={v => `${v.toLocaleString('fr-FR')} €`} />
          <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 10, fill: COLORS.primary }} />
          <Tooltip formatter={(v) => [`${Number(v).toLocaleString('fr-FR')} €/mois`, 'Impact']} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
          <Bar dataKey="euros" fill={COLORS.red} radius={[0, 4, 4, 0]} barSize={16} fillOpacity={0.8} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
